import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams, createSearchParams } from "react-router-dom";
import { Button } from "../../../components/button";
import { Header } from "../../../components/header";
import { Select, SearchBar } from "../../../components";
import { SendIcon } from "../../../components/icons";
import { useUsers } from "../hooks";
import { useMe } from "../hooks/useMe";
import { cleanObject } from "../../../helpers/clean-object";
import { UserRole } from "../../../__generated__/globalTypes";
import { userRoleLabel } from "../../../helpers/enum-labels";

interface ISearchForm {
  search?: string;
  role?: string;
}

export const Users: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: meData } = useMe();
  const isAdmin = meData?.me?.role === UserRole.Admin;

  const form = useForm<ISearchForm>({
    mode: "all",
    defaultValues: {
      search: searchParams.get("search") ?? "",
      role: searchParams.get("role") ?? "",
    },
  });

  const onSubmit = () => {
    const values = form.getValues();
    navigate({
      pathname: "/users",
      search: `?${createSearchParams(cleanObject(values))}`,
    });
  };

  const where: any = {};
  const search = searchParams.get("search");
  const role = searchParams.get("role");
  if (search) where.search = search;
  if (role) where.role = role;

  const limit = 25;
  const { data, loading, fetchMore } = useUsers({
    where,
    limit,
    offset: 0,
  });

  return (
    <>
      <Header
        title="Utilisateurs"
        subtitle="Liste des utilisateurs"
        buttons={
          isAdmin
            ? [
                {
                  actionText: "Nouvel utilisateur",
                  bgColor: "indigo",
                  textColor: "white",
                  link: `/user/create`,
                  icon: <SendIcon />,
                },
              ]
            : []
        }
      />

      <div className="main-container">
        <SearchBar
          form={form}
          onSubmit={onSubmit}
          placeholder="Rechercher un utilisateur..."
          advancedFilters={
            <Select label="Rôle" className="w-full" {...form.register("role")}>
              <option value="">Tous</option>
              {Object.keys(UserRole).map((role) => (
                <option key={role} value={role}>
                  {userRoleLabel(role)}
                </option>
              ))}
            </Select>
          }
        />

        <div className="p-4 mb-6 bg-white shadow rounded overflow-x-auto">
          <table className="table-auto w-full">
            <thead>
              <tr className="text-xs text-gray-500 text-left">
                <th className="padding-table   font-medium  ">Nom</th>
                <th className="padding-table  font-medium text-center">Rôle</th>
              </tr>
            </thead>
            <tbody>
              {data?.users?.results?.map((user, index) => (
                <tr
                  key={`user-${user.id}`}
                  className={`text-xs   ${index % 2 ? "" : "bg-gray-50"} `}
                >
                  <td className="padding-table  flex ">
                    <div>
                      <p
                        className="font-medium  cursor-pointer"
                        onClick={() => navigate(`/user/${user.id}`)}
                      >
                        {user.firstname} {user.lastname}
                      </p>
                      <p className="text-gray-500">{user.email}</p>
                    </div>
                  </td>
                  <td className="padding-table  text-center">
                    <span className="inline-block py-1 px-2 text-white bg-green-500 rounded-full">
                      {userRoleLabel(user.role)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data?.users?.hasMore && (
        <div>
          <Button
            disabled={loading}
            loading={loading}
            onClick={() => {
              fetchMore({
                variables: {
                  offset: data?.users?.results?.length,
                  limit,
                  where,
                },
              });
            }}
          >
            Plus
          </Button>
        </div>
      )}
    </>
  );
};
